/**
 * dictation — English dictation playback screen.
 *
 * Source of truth: docs/spec/LLM_WIKI_REVIEW_SPEC.md §7.2.
 *
 * The app reads each English word aloud (TTS); the student spells it on the
 * paper (the double-sided review sheet's back side), NOT on screen. We never
 * render the English word itself — only the item number + an optional Chinese
 * hint. This mirrors the printed paper flow (D4).
 *
 * 視覺 = 學術風格設計系統(src/learn/ui):紙感頁殼 `pageShell` + 學術按鈕
 * `academicButton`(見 wiki page-470f59c0)。
 * 專業介面鐵則:無音效(指 UI 點擊音 / 提示音,聽寫朗讀本身是功能)、無彩光、
 * 無 mascot、無戲謔文案。
 * 學習/遊戲分離:純學習側,不觸碰任何戰鬥數值。
 */

import type { DictationItem } from './types';
import { speak, stopSpeaking } from '../../shared/tts-client';
import { mountEscapeButton } from '../escape-button';
import { pageShell, academicButton } from '../ui/components';

/**
 * 念法1(雲端 Aura-2)的人聲池。老闆 2026-06-09「1~5 隨機」:每次播放從這 5 個
 * 清晰嗓裡隨機抽一個,讓小孩習慣不同人的發音(聽力更穩)。排除 Aura-2 原生
 * 預設 luna(老闆嫌「很爛」)。某次聽起來怪 → 改按念法2(裝置嗓)。
 */
const CLOUD_SPEAKERS = ['asteria', 'thalia', 'andromeda', 'athena', 'orion'] as const;

/** 隨機抽一個雲端嗓。 */
function pickCloudSpeaker(): string {
  return CLOUD_SPEAKERS[Math.floor(Math.random() * CLOUD_SPEAKERS.length)];
}

/**
 * Render the dictation playback screen into `container`.
 * Auto-plays the first item; advancing auto-plays the next; after the last
 * item the 「完成」 button calls `onDone`.
 */
export function runDictation(
  container: HTMLElement,
  items: DictationItem[],
  onDone: () => void,
): void {
  container.innerHTML = '';

  const shell = pageShell(container, {
    title: '英文聽寫',
    subtitle: '聽發音,在考卷上拼出單字 — 螢幕不會顯示英文,只給題號與中文提示。',
  });

  const root = document.createElement('div');
  root.className = 'dictation-screen';
  root.style.cssText = 'display:flex;flex-direction:column;gap:18px;';
  shell.body.appendChild(root);

  // 題號:大字、serif,當這張「講義」的視覺主體。
  const progressEl = document.createElement('div');
  progressEl.className = 'dictation-progress';
  progressEl.style.cssText =
    'font-family:var(--ef-serif);font-weight:700;font-size:22px;color:var(--ef-ink);letter-spacing:1px;';
  root.appendChild(progressEl);

  // 中文提示(可空);用紙片卡的留白感呈現,維持講義紙感。
  const hintEl = document.createElement('div');
  hintEl.className = 'dictation-hint';
  hintEl.style.cssText =
    'min-height:64px;display:flex;align-items:center;padding:18px 20px;font-size:20px;' +
    'color:var(--ef-ink-soft);background:var(--ef-paper-bright);border:1px solid var(--ef-line);' +
    'border-radius:var(--ef-radius);box-shadow:var(--ef-shadow-card);';
  root.appendChild(hintEl);

  const controls = document.createElement('div');
  controls.className = 'dictation-controls';
  controls.style.cssText = 'display:flex;flex-wrap:wrap;gap:10px;';
  root.appendChild(controls);

  // Two pronunciations to choose from: if one engine mispronounces a word,
  // tap the other. 念法1 = 自然嗓(雲端);念法2 = 裝置清晰嗓。
  const voice1Btn = makeButton('🔊 念法1(自然)', 'dictation-voice1', 'secondary');
  const voice2Btn = makeButton('🔊 念法2(清晰)', 'dictation-voice2', 'secondary');
  const slowBtn = makeButton('🐢 慢速', 'dictation-slow', 'secondary');
  const nextBtn = makeButton('下一題 →', 'dictation-next', 'primary');
  const doneBtn = makeButton('完成', 'dictation-done', 'primary');
  controls.append(voice1Btn, voice2Btn, slowBtn, nextBtn, doneBtn);

  const tipEl = document.createElement('div');
  tipEl.className = 'dictation-tip';
  tipEl.textContent = '某個念法聽起來怪怪的,就換另一個念法。';
  tipEl.style.cssText = 'font-size:14px;color:var(--ef-sub);line-height:1.6;';
  root.appendChild(tipEl);

  const SLOW_RATE = 0.6;
  let current = 0;

  const unmountEscape = mountEscapeButton(() => {
    cleanup();
    onDone();
  });

  const cleanup = (): void => {
    stopSpeaking();
    unmountEscape();
  };

  /** Speak the current word. `voice` picks the TTS engine; `rate` the speed. */
  const play = (
    voice: 'cloud' | 'device' | 'auto' = 'auto',
    rate = 1,
  ): void => {
    const item = items[current];
    if (!item) return;
    // 雲端/auto 隨機抽一個 speaker;device 不帶(由系統挑嗓)。
    const speaker = voice === 'device' ? undefined : pickCloudSpeaker();
    // Never display item.word — only speak it.
    void speak(item.word, { lang: 'en', rate, voice, speaker });
  };

  const render = (): void => {
    const total = items.length;
    const item = items[current];
    progressEl.textContent = `第 ${current + 1} / ${total} 題`;
    // Hint is optional; render the Chinese hint only, never the English word.
    hintEl.textContent = item?.hintZh ?? '';

    const isLast = current >= total - 1;
    nextBtn.style.display = isLast ? 'none' : '';
    doneBtn.style.display = isLast ? '' : 'none';
  };

  // 念法1 = 雲端自然嗓;念法2 = 裝置內建清晰嗓。
  voice1Btn.addEventListener('click', () => play('cloud'));
  voice2Btn.addEventListener('click', () => play('device'));
  // 慢速用裝置嗓 + 0.6 rate(裝置端 rate 較可靠)。
  slowBtn.addEventListener('click', () => play('device', SLOW_RATE));

  nextBtn.addEventListener('click', () => {
    if (current >= items.length - 1) return;
    current += 1;
    render();
    play('auto');
  });

  doneBtn.addEventListener('click', () => {
    cleanup();
    onDone();
  });

  // Enter the screen: render item 1 and auto-play it (念法1 / auto).
  render();
  if (items.length > 0) play('auto');
}

/**
 * 學術按鈕 + 既有 dictation-* class(測試 / 行為依賴);primary = 主要操作,
 * secondary = 念法 / 慢速。
 */
function makeButton(
  label: string,
  className: string,
  variant: 'primary' | 'secondary',
): HTMLButtonElement {
  const btn = academicButton({ label, variant, onClick: () => {} });
  btn.classList.add(className);
  return btn;
}
